import { z } from "zod";
import type { AssistantInterpretation, Service } from "./types";

export const ASSISTANT_MODEL = "gemini-3.5-flash-lite";
/** Same price tier; used only when the primary model is overloaded (503/429). */
export const ASSISTANT_FALLBACK_MODEL = "gemini-3.1-flash-lite";

export const interpretationSchema = z.object({
  intent: z.enum(["book", "reschedule", "cancel", "question", "urgent", "other"]),
  serviceId: z.string().nullable(),
  urgency: z.enum(["none", "soon", "urgent"]),
  preferredDays: z.array(z.number().int().min(1).max(7)),
  timeOfDay: z.enum(["morning", "afternoon", "any"]),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  summary: z.string().max(240),
  needsHuman: z.boolean(),
  confidence: z.number().min(0).max(1),
});

/**
 * Removes direct identifiers before text leaves our infrastructure (blueprint §14).
 * Names are not reliably detectable; the UI asks for contact data in separate fields.
 */
export function redactIdentifiers(text: string) {
  return text
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, "[email]")
    .replace(/\b\d{1,2}\.?\d{3}\.?\d{3}\b/g, "[documento]")
    .replace(/(\+?\d[\d\s()-]{7,}\d)/g, "[teléfono]")
    .replace(/\b(me llamo|soy|mi nombre es)\s+[A-ZÁÉÍÓÚÑ][\wáéíóúñ]+(\s+[A-ZÁÉÍÓÚÑ][\wáéíóúñ]+)?/gi, "$1 [nombre]");
}

export function assistantSystemPrompt(services: Service[], todayLocal: string, weekdayName: string) {
  const catalog = services
    .filter((s) => s.active)
    .map((s) => `- ${s.id}: ${s.name} (${s.summary})${s.bookableOnline ? "" : " [requiere evaluación previa, no reservable online]"}`)
    .join("\n");
  return `Sos el asistente de turnos de COP (Consultorio Odontológico Pilar), Argentina.
Tu única tarea es interpretar el pedido del paciente y devolver JSON estructurado. No das diagnósticos, no recomendás medicación ni tratamientos, no inventás disponibilidad.
Hoy es ${weekdayName} ${todayLocal} (zona America/Argentina/Buenos_Aires).

Servicios disponibles (usá exactamente el id o null si no corresponde):
${catalog}

Reglas:
- intent: "book" pedir turno nuevo; "reschedule" cambiar uno existente; "cancel" cancelar; "question" consulta general; "urgent" dolor intenso, sangrado, traumatismo, hinchazón o fractura; "other".
- Si hay dolor intenso, traumatismo, sangrado persistente o hinchazón: intent "urgent", urgency "urgent", serviceId "urgencia", needsHuman true.
- preferredDays: días ISO (1=lunes … 7=domingo) mencionados; vacío si no hay preferencia.
- timeOfDay: "morning" antes de las 13, "afternoon" después, "any" si no se indica.
- dateFrom/dateTo: rango de fechas YYYY-MM-DD derivado de expresiones como "la semana que viene" o "el jueves"; null si no hay.
- summary: una oración en español rioplatense, sin datos personales.
- needsHuman: true si el pedido es ambiguo, clínico, administrativo (obra social, presupuesto) o no es sobre turnos.
- confidence: 0 a 1.`;
}

const KEYWORDS: [RegExp, string][] = [
  [/dolor|urgen|me duele|golpe|fractur|hinchad|sangr/i, "urgencia"],
  [/limpieza|profilaxis|sarro/i, "limpieza"],
  [/blanque|carilla|estétic|estetic|sonrisa/i, "estetica-evaluacion"],
  [/ortodon|bracket|alineador/i, "ortodoncia-evaluacion"],
  [/implante|perd[ií] (un|una) (diente|muela)/i, "implantes-evaluacion"],
  [/hij[oa]|nen[ea]|niñ[oa]|chic[oa]s?/i, "odontopediatria"],
  [/encía|encia|periodon/i, "periodoncia-evaluacion"],
  [/conducto|endodon/i, "endodoncia"],
  [/control/i, "control"],
  [/consulta|revis|chequeo|primera vez/i, "consulta"],
];

const DAYS: [RegExp, number][] = [
  [/lunes/i, 1],
  [/martes/i, 2],
  [/mi[eé]rcoles/i, 3],
  [/jueves/i, 4],
  [/viernes/i, 5],
  [/s[aá]bado/i, 6],
];

/** Deterministic fallback used when no Gemini key is configured (local emulator / outage). */
export function heuristicInterpretation(text: string): AssistantInterpretation {
  const serviceId = KEYWORDS.find(([re]) => re.test(text))?.[1] ?? null;
  const urgent = serviceId === "urgencia";
  const intent = urgent
    ? "urgent"
    : /cancel|anular|no voy a poder/i.test(text)
      ? "cancel"
      : /cambiar|reprogram|mover|pasar (el|mi) turno/i.test(text)
        ? "reschedule"
        : /turno|reserv|agend|consulta|limpieza|quiero/i.test(text)
          ? "book"
          : "question";
  return {
    intent,
    serviceId,
    urgency: urgent ? "urgent" : "none",
    preferredDays: DAYS.filter(([re]) => re.test(text)).map(([, d]) => d),
    timeOfDay: /mañana|temprano/i.test(text) ? "morning" : /tarde|después de las/i.test(text) ? "afternoon" : "any",
    dateFrom: null,
    dateTo: null,
    summary: "Interpretación automática básica (modo sin IA).",
    needsHuman: urgent || intent === "question" || serviceId === null,
    confidence: serviceId ? 0.5 : 0.2,
  };
}
